// useCurrentMerchantStatus — resolves the status of the currently-selected
// merchant from the two sources that carry it:
//
//   1. /v1/me memberships[]   — for merchant-tier users, includes status
//      via the server-side merchants JOIN (commit 56eeae2).
//   2. /v1/merchants list     — for admins (no memberships rows).
//
// Returns null when no merchant is selected or no source resolves the id.
//
// PDF round 2 ("show banner and block payouts"): consumed by the banner
// at the top of the authed shell and by the disabled-state on the
// "New payout" button. Server-side enforcement lives in payout.Service
// (Initiate/BatchInitiate/Approve/BatchApprove); this is purely UX
// pre-emption so operators see the gate before submitting.
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";
import { useMerchants } from "@/queries/useMerchants";

/**
 * The four canonical merchant.status values from the backend. Anything
 * else falls through to null (treated as "no banner, no block").
 */
export type MerchantStatus = "active" | "suspended" | "closed";

export interface CurrentMerchantState {
  merchantId: number | null;
  /** Display name when known; falls back to "Merchant #ID". */
  label: string | null;
  status: MerchantStatus | null;
}

export function useCurrentMerchantStatus(): CurrentMerchantState {
  const memberships = useAuth((s) => s.user?.memberships ?? []);
  const merchantId = useUI((s) => s.currentMerchantId);
  // useMerchants is admin-only (gated inside the hook). For non-admins
  // it returns []; the merchant's own membership row carries status.
  const { data: merchants = [] } = useMerchants();

  if (merchantId == null) {
    return { merchantId: null, label: null, status: null };
  }

  // Try the admin path first (cross-tenant view).
  const adminRow = merchants.find((m) => m.id === merchantId);
  if (adminRow) {
    return {
      merchantId,
      label: adminRow.display_name,
      status: normalize(adminRow.status),
    };
  }

  // Fall back to the merchant's own membership row.
  const m = memberships.find((x) => x.merchant_id === merchantId);
  if (m) {
    return {
      merchantId,
      label: m.display_name ?? `Merchant #${merchantId}`,
      status: normalize(m.status ?? null),
    };
  }

  return { merchantId, label: `Merchant #${merchantId}`, status: null };
}

function normalize(s: string | null | undefined): MerchantStatus | null {
  if (s === "active" || s === "suspended" || s === "closed") return s;
  return null;
}

/** True when payouts/approve actions should be blocked client-side. */
export function isMerchantBlocked(s: MerchantStatus | null): boolean {
  return s === "suspended" || s === "closed";
}
